import type { Erreur } from '@/features/domaine/moteur-regles'
import type {
  ErreurSummaryCallbackResult,
  ErreurSummaryConfiguration,
  ErreurSummaryHandlers,
} from './ErreurSummaryConfiguration'
import type { ErreurWithDirty } from '../dirty'
import type { ErreurSummary, ErreurSummaryResult } from './ErreurSummary'

const EMPTY_RESULT: ErreurSummaryResult = {
  $estErreur: false,
  $nbErreurs: 0,
  $estErreurDirty: false,
  $nbErreursDirty: 0,
}

export class ErreurTabSummarizer<TModel, TConfiguration extends ErreurSummaryConfiguration<TModel>> {
  readonly #configuration: TConfiguration
  readonly #groups: (keyof TConfiguration)[]
  constructor(configuration: TConfiguration) {
    this.#configuration = configuration
    this.#groups = Object.keys(configuration)
  }

  execute(erreurs: Erreur<TModel> | ErreurWithDirty<TModel>) {
    const results = {} as ErreurSummary<keyof TConfiguration>

    for (const groupe of this.#groups) {
      // Recupération de la configuation du groupe
      const handlers = (this.#configuration as any)[groupe] as ErreurSummaryHandlers<TModel>
      const summarize = new ErreurSummarizer(handlers)
      ;(results as any)[groupe] = summarize.execute(erreurs)
    }

    return results
  }
}

export class ErreurSummarizer<TModel> {
  readonly #handlers: ErreurSummaryHandlers<TModel>
  constructor(handlers: ErreurSummaryHandlers<TModel>) {
    this.#handlers = handlers
  }
  execute(erreurs: Erreur<TModel> | ErreurWithDirty<TModel>): ErreurSummaryResult {
    if (!erreurs) return { ...EMPTY_RESULT }

    const includeErreurList = this.#handlers.include?.call(null, erreurs)
    const includeDeepErreurList = this.#handlers.includeDeep?.call(null, erreurs)
    const excludeErreurList = this.#handlers.exclude?.call(null, erreurs)
    const excludeDeepErreurList = this.#handlers.excludeDeep?.call(null, erreurs)

    const include = this.summarizeListErreurs(includeErreurList, false)
    const includeDeep = this.summarizeListErreurs(includeDeepErreurList, true)
    const exclude = this.summarizeListErreurs(excludeErreurList, false)
    const excludeDeep = this.summarizeListErreurs(excludeDeepErreurList, true)

    const sumInclude = ErreurSummarizer.addErreurs(include, includeDeep)
    const sumExclude = ErreurSummarizer.addErreurs(exclude, excludeDeep)

    return ErreurSummarizer.removeErreurs(sumInclude, sumExclude)
  }

  public summarizeListErreurs(
    erreurList: ErreurSummaryCallbackResult<TModel>[] | undefined,
    deep: boolean,
  ): ErreurSummaryResult {
    if (!erreurList) return { ...EMPTY_RESULT }

    return erreurList
      .map((erreur) => this.summarizeErreur(erreur, deep))
      .reduce(ErreurSummarizer.addErreurs, { ...EMPTY_RESULT })
  }

  public summarizeErreur(erreur: ErreurSummaryCallbackResult<TModel> | undefined, deep: boolean): ErreurSummaryResult {
    if (erreur?.$estErreur !== true) return { ...EMPTY_RESULT }
    const rootResult = this.extractErreur(erreur)
    if (!deep) return rootResult

    return Object.keys(erreur)
      .filter((key) => key.startsWith('$') === false)
      .map((key) => {
        const erreurEnfant = (erreur as any)[key] as ErreurSummaryCallbackResult<any>
        if (Array.isArray(erreurEnfant)) {
          const arrRootResult = this.summarizeErreur(erreurEnfant, false)
          return erreurEnfant
            .map((erreurPetitEnfant) => {
              return this.summarizeErreur(erreurPetitEnfant, true)
            })
            .reduce(ErreurSummarizer.addErreurs, arrRootResult)
        } else {
          return this.summarizeErreur(erreurEnfant, true)
        }
      })
      .reduce(ErreurSummarizer.addErreurs, rootResult)
  }

  private extractErreur(erreur: Exclude<ErreurSummaryCallbackResult<TModel>, undefined>): ErreurSummaryResult {
    const estErreur = !!erreur.$messageErreur

    if ('$estErreurDirty' in erreur) {
      const estErreurDirty = erreur.$estErreurDirty && !!erreur.$messageErreurDirty
      return {
        $estErreur: estErreur,
        $nbErreurs: estErreur ? 1 : 0,
        $estErreurDirty: estErreurDirty,
        $nbErreursDirty: estErreurDirty ? 1 : 0,
      } as ErreurSummaryResult
    } else {
      return {
        $estErreur: estErreur,
        $nbErreurs: estErreur ? 1 : 0,
        $estErreurDirty: false,
        $nbErreursDirty: 0,
      } as ErreurSummaryResult
    }
  }

  private static addErreurs(err1: ErreurSummaryResult, err2: ErreurSummaryResult): ErreurSummaryResult {
    return {
      $estErreur: err1.$estErreur || err2.$estErreur,
      $nbErreurs: err1.$nbErreurs + err2.$nbErreurs,
      $estErreurDirty: err1.$estErreurDirty || err2.$estErreurDirty,
      $nbErreursDirty: err1.$nbErreursDirty + err2.$nbErreursDirty,
    }
  }

  private static removeErreurs(err1: ErreurSummaryResult, err2: ErreurSummaryResult): ErreurSummaryResult {
    const nbErreurs = err1.$nbErreurs - err2.$nbErreurs
    if (nbErreurs < 0) {
      console.error("Le nombre d'erreur ne peut etre negatif. Verifier votre paramétrage.")
      throw "Nombre d'erreur incohérent"
    }
    const nbErreursDirty = err1.$nbErreursDirty - err2.$nbErreursDirty
    return {
      $estErreur: nbErreurs > 0,
      $nbErreurs: nbErreurs,
      $estErreurDirty: nbErreursDirty > 0,
      $nbErreursDirty: nbErreursDirty,
    }
  }
}

